import { useEffect, useState } from "react";
import omit from "lodash.omit";
import omitBy from "lodash.omitby";
import isNull from "lodash.isnull";
import { useCurrentAccountId } from "@zapier/identity";
import { DEFAULT_VIEW_SETTINGS } from "@zapier/utils";
import { DefaultViewSelection, DefaultViewSelectionFilters } from "./types";

type OldDefaultViewSelection = {
  pathname: keyof DefaultViewSelection;
} & DefaultViewSelectionFilters;

const STORAGE_KEY = (accountId: number | undefined) =>
  `${DEFAULT_VIEW_SETTINGS}-${accountId}`;

/**
 * Migrates old default view format to new format
 * Old format: { pathname: string, ...filters }
 * New format: { [pathname]: { ...filters } }
 */
function migrateOldFormatIfNeeded(currentAccountId: number | undefined): void {
  if (globalThis.window === undefined) {
    return;
  }

  const storedData = localStorage.getItem(STORAGE_KEY(currentAccountId));
  if (!storedData) {
    return;
  }

  try {
    const parsedData = JSON.parse(storedData);

    // Check if it's the old format (has pathname property at root)
    if (parsedData.pathname) {
      const oldFormat = parsedData as OldDefaultViewSelection;
      const migratedPathname = oldFormat.pathname;
      const filters = omitBy(omit(oldFormat, ["pathname"]), isNull);

      // Save in new format
      const newFormat = {
        [migratedPathname]: filters,
      };

      localStorage.setItem(
        STORAGE_KEY(currentAccountId),
        JSON.stringify(newFormat),
      );
    }
  } catch (error) {
    // If migration fails, just continue - don't break the app
    console.warn("Failed to migrate default view settings:", error);
  }
}

/**
 * Migrates saved default view settings from the old "app/assets/interfaces"
 * key to the new "app/assets/forms" key after the Interfaces → Forms rebrand.
 */
function migrateInterfacesToForms(currentAccountId: number | undefined): void {
  if (globalThis.window === undefined) {
    return;
  }

  const storedData = localStorage.getItem(STORAGE_KEY(currentAccountId));
  if (!storedData) {
    return;
  }

  try {
    const parsedData = JSON.parse(storedData);
    const OLD_KEY = "app/assets/interfaces";
    const NEW_KEY = "app/assets/forms";

    if (parsedData[OLD_KEY] && !parsedData[NEW_KEY]) {
      const migrated = {
        ...omit(parsedData, [OLD_KEY]),
        [NEW_KEY]: parsedData[OLD_KEY],
      };
      localStorage.setItem(
        STORAGE_KEY(currentAccountId),
        JSON.stringify(migrated),
      );
    }
  } catch {
    // If migration fails, just continue - don't break the app
  }
}

/**
 * Hook to handle one-time migration of default view settings
 * Runs once on mount and migrates old format to new format if needed
 * @returns isReady - true when migration is complete
 */
export function useDefaultViewMigration(): boolean {
  const currentAccountId = useCurrentAccountId();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    migrateOldFormatIfNeeded(currentAccountId);
    migrateInterfacesToForms(currentAccountId);
    setIsReady(true);
  }, [currentAccountId]);

  return isReady;
}
